import React, { useEffect, useState } from "react";
import { message, Card, Drawer, Table, Row, Col, Dropdown, Modal } from "antd";
import { Text } from "react-font";
import "jspdf-autotable";
import { init } from "emailjs-com";
import {
	Button as EvergreenButton,
	SearchInput,
	Badge,
} from "evergreen-ui";
import FireFetch from "../Firebase/FireFetch";
import Firebase from "../Firebase/Firebase";
import { TableExport } from "tableexport";
import AddUser from "../Modals/Users/AddUser";
import EditUser from "../Modals/Users/EditUser";
import ViewUser from "../Modals/Users/ViewUser";
import { getAuth } from "firebase/auth";
import { EllipsisOutlined } from "@ant-design/icons";
import { useAuth } from "../AuthContext/AuthContext";
import {
	getUserStatusBadgeColor,
	getUserStatusLabel,
} from "../../constants/userStatus";
import { getCallable } from "../Firebase/functionsClient";

init("user_2JD2DZg8xAHDW7e9kdorr");

const auth = getAuth(Firebase);

const mapUserToTableRow = (u = {}, index = 0, existingKey) => ({
	key: existingKey || u.key || u.id || index + 1,
	id: u.id,
	name:
		`${u.firstname || ""} ${u.surname || ""}`.trim() ||
		u.name ||
		"",
	phone: u.phone || "",
	email: u.email || "",
	userRole: u.userRole || u.role || "",
	org: u.organisationName || u.orgName || "",
	community: u.communityName || "",
	country: u.countryName || "",
	userStatus: getUserStatusLabel(u.userStatus || u.status || ""),
	canViewPrivate:
		typeof u.canViewPrivate === "boolean" ? u.canViewPrivate : true,
	originalUser: u,
});

const mapUsersToTableRows = (users = []) =>
	users.map((u, index) => mapUserToTableRow(u, index));

const filterUsers = (users = [], value = "") => {
	const searchText = (value || "").toLowerCase();

	if (!searchText) {
		return users;
	}

	return users.filter((row) => {
		const name = (row.name || "").toLowerCase();
		const role = (row.userRole || "").toLowerCase();
		const status = getUserStatusLabel(row.userStatus).toLowerCase();
		const phone = String(row.phone || "").toLowerCase();
		const email = String(row.email || "").toLowerCase();
		const org = (row.org || "").toLowerCase();
		const community = (row.community || "").toLowerCase();
		const country = (row.country || "").toLowerCase();

		return (
			name.includes(searchText) ||
			role.includes(searchText) ||
			status.includes(searchText) ||
			phone.includes(searchText) ||
			email.includes(searchText) ||
			org.includes(searchText) ||
			community.includes(searchText) ||
			country.includes(searchText)
		);
	});
};

const Users = () => {
	const [allUsers, setAllUsers] = useState([]);
	const [filteredUsers, setFilteredUsers] = useState([]);
	const [searchValue, setSearchValue] = useState("");
	const [loading, setLoading] = useState(false);
	const { canAccessModule } = useAuth();
	const canCreateUser = canAccessModule("USERS", "create");
	const canUpdateUser = canAccessModule("USERS", "update");
	const canDeleteUser = canAccessModule("USERS", "delete");
	const [showCreateDrawer, setShowCreateDrawer] = useState(false);
	const [showEditDrawer, setShowEditDrawer] = useState(false);
	const [showViewDrawer, setShowViewDrawer] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);

	const listUsers = getCallable("listUsers");
	const deleteUserById = getCallable("deleteUser");

	const loadUsers = async () => {
		const currentUser = auth.currentUser;
		if (!currentUser) return;

		setLoading(true);
		try {
			const res = await listUsers({});
			const data = res.data || {};
			const users = data.users || [];
			const tableRows = mapUsersToTableRows(users);

			setAllUsers(tableRows);
		} catch (err) {
			console.error("Error loading users:", err);
			message.error("Unable to load users. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadUsers();
	}, []);

	useEffect(() => {
		setFilteredUsers(filterUsers(allUsers, searchValue));
	}, [allUsers, searchValue]);

	const handleSearch = (value) => {
		setSearchValue(value || "");
	};

	const handleUserUpdated = (updatedUser) => {
		if (!updatedUser?.id) {
			return;
		}

		setAllUsers((previousUsers) =>
			previousUsers.map((row, index) => {
				if (row.id !== updatedUser.id) {
					return row;
				}

				const mergedUser = {
					...row.originalUser,
					...updatedUser,
				};

				return mapUserToTableRow(mergedUser, index, row.key);
			})
		);

		setSelectedUser((previousUser) =>
			previousUser?.id === updatedUser.id
				? {
					...previousUser,
					...updatedUser,
				}
				: previousUser
		);
	};

	const deleteUser = async (objectID) => {
		if (!canDeleteUser) {
			message.error("You do not have permission to delete users.");
			return;
		}

		if (!objectID) {
			message.error("Unable to delete user. Missing user id.");
			return;
		}

		try {
			await deleteUserById({ userId: objectID });
			setAllUsers((previousUsers) => previousUsers.filter((row) => row.id !== objectID));
			setSelectedUser((previousUser) =>
				previousUser?.id === objectID ? null : previousUser
			);
			message.warning("User deleted successfully.");
		} catch (error) {
			console.error("Error deleting user:", error);
			if (error?.code === "functions/permission-denied") {
				message.error("You do not have permission to delete this user.");
				return;
			}

			if (error?.code === "functions/not-found") {
				message.error("User not found.");
				return;
			}

			message.error("Unable to delete user. An error occurred.");
		}
	};

	const confirmDeleteUser = (record) => {
		Modal.confirm({
			title: "Delete this user?",
			content: "This action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: () => deleteUser(record.id),
		});
	};

	const columns = [
		{
			title: "Fullname",
			dataIndex: "name",
			key: "name",
			sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
		},
		{
			title: "Phone",
			dataIndex: "phone",
			key: "phone",
			sorter: (a, b) =>
				String(a.phone || "").localeCompare(String(b.phone || "")),
			render: (value, record) =>
				record.canViewPrivate ? (
					value
				) : (
					<Badge color="neutral">Hidden</Badge>
				),
		},
		{
			title: "Email",
			dataIndex: "email",
			key: "email",
			sorter: (a, b) =>
				String(a.email || "").localeCompare(String(b.email || "")),
			render: (value, record) =>
				record.canViewPrivate ? (
					value
				) : (
					<Badge color="neutral">Hidden</Badge>
				),
		},
		{
			title: "User Role",
			dataIndex: "userRole",
			key: "userRole",
			sorter: (a, b) =>
				(a.userRole || "").localeCompare(b.userRole || ""),
		},
		{
			title: "Organization",
			dataIndex: "org",
			key: "org",
			sorter: (a, b) => (a.org || "").localeCompare(b.org || ""),
		},
		{
			title: "Community",
			dataIndex: "community",
			key: "community",
			sorter: (a, b) =>
				(a.community || "").localeCompare(b.community || ""),
		},
		{
			title: "Country",
			dataIndex: "country",
			key: "country",
			sorter: (a, b) =>
				(a.country || "").localeCompare(b.country || ""),
		},
		{
			title: "Status",
			dataIndex: "userStatus",
			key: "userStatus",
			sorter: (a, b) =>
				(a.userStatus || "").localeCompare(b.userStatus || ""),
			render: (value) => (
				<Badge color={getUserStatusBadgeColor(value)}>
					{getUserStatusLabel(value) || "Unknown"}
				</Badge>
			),
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			render: (value, record) => {
				const items = [
					{
						key: "view",
						label: "View",
					},
					...(canUpdateUser
						? [
							{
								key: "edit",
								label: "Edit",
							},
						]
						: []),
					...(canDeleteUser
						? [
							{
								key: "delete",
								label: "Delete",
								danger: true,
							},
						]
						: []),
				];

				return (
					<Dropdown
						trigger={["click"]}
						menu={{
							items,
							onClick: ({ key, domEvent }) => {
								domEvent.stopPropagation();

								if (key === "view") {
									setSelectedUser(record.originalUser);
									setShowViewDrawer(true);
									return;
								}

								if (key === "edit") {
									setSelectedUser(record.originalUser);
									setShowEditDrawer(true);
									return;
								}

								if (key === "delete") {
									confirmDeleteUser(record);
								}
							},
						}}
					>
						<EvergreenButton
							height={32}
							minWidth={32}
							paddingLeft={8}
							paddingRight={8}
							onClick={(event) => event.stopPropagation()}
						>
							<EllipsisOutlined />
						</EvergreenButton>
					</Dropdown>
				);
			},
		},
	];

	return (
		<div className="py-4 mx-1 mt-4">
			<Col>
				<Drawer
					id="view-user"
					title="View User"
					width={600}
					maskClosable
					closable
					className="font-Nunito"
					placement="right"
					onClose={() => setShowViewDrawer(false)}
					open={showViewDrawer}
				>
					{selectedUser && (
						<ViewUser
							selectedUser={selectedUser}
							onClose={() => setShowViewDrawer(false)}
						/>
					)}
				</Drawer>

				<Drawer
					id="add-user"
					title="New User"
					width={600}
					maskClosable
					className="font-Nunito"
					placement="right"
					onClose={() => setShowCreateDrawer(false)}
					open={showCreateDrawer}
				>
					<AddUser modal={setShowCreateDrawer} />
				</Drawer>

				<Drawer
					title="Edit User"
					width={600}
					maskClosable
					className="font-Nunito"
					placement="right"
					onClose={() => setShowEditDrawer(false)}
					open={showEditDrawer}
				>
					{selectedUser && (
						<EditUser
							key={selectedUser.id || "edit-user"}
							user={selectedUser}
							modal={setShowEditDrawer}
							onUserUpdated={handleUserUpdated}
						/>
					)}
				</Drawer>

				<Row gutter={[4, 6]}>
					<Col span={24}>
						<Card style={{ borderRadius: 15 }}>
							<Row>
								<Col span={24}>
									<div className="text-left">
										<h5
											className="font-weight-bold"
											style={{ color: "#51195D" }}
										>
											<Text family="Nunito">Users</Text>
										</h5>
									</div>

									<Row className="ml-1">
										<div className="d-flex flex-row">
											<SearchInput
												height={40}
												placeholder="Search Users (name, phone, email, role, status, org, community, country)"
												width={window.innerWidth / 4}
												onChange={(e) => handleSearch(e.target.value)}
											/>
											{canCreateUser && (
												<EvergreenButton
													height={40}
													appearance="primary"
													className="mx-2"
													onClick={() => setShowCreateDrawer(true)}
												>
													New User
												</EvergreenButton>
											)}
											<EvergreenButton
												height={40}
												className="mx-2"
												onClick={() => {
													const tableElement =
														document.getElementById("dataTable");
													if (!tableElement) return;

													const exporter = TableExport(tableElement, {
														filename: "Users",
														exportButtons: false,
														sheetname: "Users",
													});

													// Use filteredUsers so export respects search
													FireFetch.exportXL(
														exporter,
														filteredUsers.slice()
													);
												}}
											>
												Print
											</EvergreenButton>
										</div>
									</Row>
								</Col>
							</Row>
						</Card>

						<Card style={{ borderRadius: 15 }}>
							<Card className="d-flex flex-column overflow-auto">
								<Table
									style={{ overflow: "auto" }}
									id="dataTable"
									className="custom-table"
									pagination={{
										defaultPageSize: 100,
										showSizeChanger: true,
										pageSizeOptions: ["100", "200", "300"],
									}}
									loading={loading}
									dataSource={filteredUsers}
									columns={columns}
									rowKey="key"
								/>
							</Card>
						</Card>
					</Col>
				</Row>
			</Col>
		</div>
	);
};

export default Users;
